import theme from 'themes';
import { createGlobalStyle, css } from 'styled-components';
import { Colors } from 'components/utils/styles/ui';

import { isStorybook } from 'utils/node-env-vars';

// TT Interphases font
import TtInterphasesRegularSrc from 'fonts/TT_Interphases_Regular.ttf';
import TtInterphasesItalicSrc from 'fonts/TT_Interphases_Italic.ttf';
import TtInterphasesDemiBoldSrc from 'fonts/TT_Interphases_DemiBold.ttf';
import TtInterphasesDemiBoldItalicSrc from 'fonts/TT_Interphases_DemiBold_Italic.ttf';
import TtInterphasesLightSrc from 'fonts/TT_Interphases_Light.ttf';
import TtInterphasesLightItalicSrc from 'fonts/TT_Interphases_Light_Italic.ttf';
import TtInterphasesMediumItalicSrc from 'fonts/TT_Interphases_Medium_Italic.ttf';
import TtInterphasesMediumSrc from 'fonts/TT_Interphases_Medium.ttf';

// Noto Color Emoji font
import NotoColorEmojiSrc from 'fonts/NotoColorEmoji.ttf';

const StorybookStyle = css`
  #root {
    padding: 1rem 0.3rem;
  }
`;

const generateListStyles = (level, counter, format) => css`
  ol > li${' > ol > li'.repeat(level - 1)} {
    counter-increment: ${counter};
    ${level !== 1 && 'padding-left: 2rem;'}
    display: flex;
    align-items: flex-start;
  }

  ol > li${' > ol > li'.repeat(level - 1)}::before {
    content: counter(${counter}, ${format}) '${format === 'decimal'
        ? '.'
        : ')'}';
    min-width: 2rem;
    display: block;
    vertical-align: middle;
  }

  ol > li${' > ol > li'.repeat(level - 1)} > p {
    display: inline;
    margin-left: 0.3rem !important;
    vertical-align: middle;
  }

  ol > li${' > ol > li'.repeat(level - 1)} > ol {
    list-style: none;
    counter-reset: ${counter};
  }
`;

const customListStyling = css`
  ol {
    /* Removing default browser numbering to introduce counters */
    list-style: none;
    counter-reset: level1;
    padding-inline-start: 0;
  }

  ${Array(15)
    .fill(0)
    .map((_, i) => {
      let format = null;

      if (i % 3 === 0) {
        format = 'decimal';
      } else if (i % 3 === 1) {
        format = 'lower-alpha';
      } else {
        format = 'lower-roman';
      }

      return generateListStyles(i + 1, `level${i + 1}`, format);
    })}
`;

const kbdStyles = `
  kbd {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    font-family: ${theme.fonts.text};
    font-size: 1.2rem;
    font-weight: 500;
    line-height: 1.6rem;
    color: ${Colors.textLight};
    background-color: ${Colors.white};
    border: 0.1rem solid ${Colors.greyDark};
    border-radius: 0.4rem;
    box-shadow: inset 0 -0.1rem 0 ${Colors.grey},
      0 0.1rem 0.2rem ${Colors.blackMoreTransparent};
    white-space: nowrap;
    vertical-align: middle;
  }
`;

const GlobalStyles = createGlobalStyle`
  /*storybook implementation*/

  @font-face {
    font-family: "TT Interphases";
    src: url("${TtInterphasesRegularSrc}");
    font-weight: 400;
  }
  @font-face {
    font-family: "TT Interphases";
    src: url("${TtInterphasesItalicSrc}");
    font-weight: 400;
    font-style: italic;
  }
  @font-face {
    font-family: "TT Interphases";
    src: url("${TtInterphasesMediumSrc}");
    font-weight: 500;
  }
  @font-face {
    font-family: "TT Interphases";
    src: url("${TtInterphasesMediumItalicSrc}");
    font-weight: 500;
    font-style: italic;
  }
  @font-face {
    font-family: "TT Interphases";
    src: url("${TtInterphasesDemiBoldSrc}");
    font-weight: 600;
  }
  @font-face {
    font-family: "TT Interphases";
    src: url("${TtInterphasesDemiBoldItalicSrc}");
    font-weight: 600;
    font-style: italic;
  }
  @font-face {
    font-family: "TT Interphases";
    src: url("${TtInterphasesLightSrc}");
    font-weight: 100;
  }
  @font-face {
    font-family: "TT Interphases";
    src: url("${TtInterphasesLightItalicSrc}");
    font-weight: 100;
    font-style: italic;
  }

  @font-face {
    font-family: "Noto Color Emoji";
    src: url("${NotoColorEmojiSrc}");
  }

  html,
  body {
    -ms-overflow-style: -ms-autohiding-scrollbar;
    font-size: 62.5%;
  }

  #app {
    min-height: 100vh;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

  ul {
    list-style: none;
  }

  a {
    text-decoration: none;
  }

  /* Custom list styling */
  ${customListStyling}

  /* normalize */
  /*! normalize.css v5.0.0 | MIT License | github.com/necolas/normalize.css */

  /**
   *  * 1. Change the default font family in all browsers (opinionated).
   *   * 2. Correct the line height in all browsers.
   *    * 3. Prevent adjustments of font size after orientation changes in
   *     *    IE on Windows Phone and in iOS.
   *      */

  /* Document
   *    ========================================================================== */

  html {
    box-sizing: border-box;
    font-family: ${theme.fonts.text};
    line-height: 1.15; /* 2 */
    -ms-text-size-adjust: 100%; /* 3 */
    -webkit-text-size-adjust: 100%; /* 3 */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-smoothing: antialiased;
  }

  *, *:before, *:after {
    box-sizing: inherit;
  }

  /* Sections
   *    ========================================================================== */

  /**
   *  * Remove the margin in all browsers (opinionated).
   *   */

  body {
    color: ${Colors.text};
    background-color: ${Colors.white};
    margin: 0;
    width: calc(100% + 6px);
    ${theme.typography.text.normal};
    font-weight: 400;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.fonts.headers};
    font-weight: 600;
  }

  /**
   *  * Add the correct display in IE 9-.
   *   */

  article,
    aside,
    footer,
    header,
    nav,
    section {
      display: block;
    }

  /* Grouping content
   *    ========================================================================== */

  /**
   *  * Add the correct display in IE 9-.
   *   * 1. Add the correct display in IE.
   *    */

  figcaption,
    figure,
    main { /* 1 */
      display: block;
    }

  /**
   *  * Add the correct margin in IE 8.
   *   */

  figure {
    margin: 1em 40px;
  }

  /**
   *  * 1. Add the correct box sizing in Firefox.
   *   * 2. Show the overflow in Edge and IE.
   *    */

  hr {
    box-sizing: content-box; /* 1 */
    height: 0; /* 1 */
    overflow: visible; /* 2 */
  }

  /**
   *  * 1. Correct the inheritance and scaling of font size in all browsers.
   *   * 2. Correct the odd 'em' font sizing in all browsers.
   *    */

  pre {
    font-family: monospace, monospace; /* 1 */
    font-size: 1em; /* 2 */
  }

  /* Text-level semantics
   *    ========================================================================== */

  /**
   *  * 1. Remove the gray background on active links in IE 10.
   *   * 2. Remove gaps in links underline in iOS 8+ and Safari 8+.
   *    */

  a {
    background-color: transparent; /* 1 */
    -webkit-text-decoration-skip: objects; /* 2 */
  }

  /**
   *  * Remove the outline on focused links when they are also active or hovered
   *   * in all browsers (opinionated).
   *    */

  a:active,
    a:hover {
      outline-width: 0;
    }

  /**
   *  * 1. Remove the bottom border in Firefox 39-.
   *   * 2. Add the correct text decoration in Chrome, Edge, IE, Opera, and Safari.
   *    */

  abbr[title] {
    border-bottom: none; /* 1 */
    text-decoration: underline; /* 2 */
    text-decoration: underline dotted; /* 2 */
  }

  /**
   *  * Prevent the duplicate application of 'bolder' by the next rule in Safari 6.
   *   */

  b,
    strong {
      font-weight: inherit;
    }

  /**
   *  * Add the correct font weight in Chrome, Edge, and Safari.
   *   */

  b,
    strong {
      font-weight: 600;
    }

  /**
   *  * 1. Correct the inheritance and scaling of font size in all browsers.
   *   * 2. Correct the odd 'em' font sizing in all browsers.
   *    */

  code,
    samp {
      font-family: monospace, monospace; /* 1 */
      font-size: 1em; /* 2 */
    }

  ${kbdStyles}

  /**
   *  * Add the correct font style in Android 4.3-.
   *   */

  dfn {
    font-style: italic;
  }

  /**
   *  * Add the correct background and color in IE 9-.
   *   */

  mark {
    background-color: ${Colors.tealLight};
    color: ${Colors.text};
  }

  /**
   *  * Add the correct font size in all browsers.
   *   */

  small {
    font-size: 80%;
  }

  /**
   *  * Prevent 'sub' and 'sup' elements from affecting the line height in
   *   * all browsers.
   *    */

  sub,
    sup {
      font-size: 75%;
      line-height: 0;
      position: relative;
      vertical-align: baseline;
    }

  sub {
    bottom: -0.25em;
  }

  sup {
    top: -0.5em;
  }

  /* Embedded content
   *    ========================================================================== */

  /**
   *  * Add the correct display in IE 9-.
   *   */

  audio,
    video {
      display: inline-block;
    }

  /**
   *  * Add the correct display in iOS 4-7.
   *   */

  audio:not([controls]) {
    display: none;
    height: 0;
  }

  /**
   *  * Remove the border on images inside links in IE 10-.
   *   */

  img {
    border-style: none;
  }

  /**
   *  * Hide the overflow in IE.
   *   */

  svg:not(:root) {
    overflow: hidden;
  }

  /* Forms
   *    ========================================================================== */

  /**
   *  * 1. Change the font styles in all browsers (opinionated).
   *   * 2. Remove the margin in Firefox and Safari.
   *    */

  button,
    input,
    optgroup,
    select,
    textarea {
      font-family: ${theme.fonts.text};
      ${theme.typography.text.normal};
      margin: 0; /* 2 */
    }

  /**
   *  * Show the overflow in IE.
   *   * 1. Show the overflow in Edge.
   *    */

  button,
    input { /* 1 */
      overflow: visible;
    }

  /**
   *  * Remove the inheritance of text transform in Edge, Firefox, and IE.
   *   * 1. Remove the inheritance of text transform in Firefox.
   *    */

  button,
    select { /* 1 */
      text-transform: none;
    }

  /**
   *  * 1. Prevent a WebKit bug where (2) destroys native 'audio' and 'video'
   *   *    controls in Android 4.
   *    * 2. Correct the inability to style clickable types in iOS and Safari.
   *     */

  button,
    html [type="button"], /* 1 */
    [type="reset"],
    [type="submit"] {
      -webkit-appearance: button; /* 2 */
    }

  /**
   *  * Remove the inner border and padding in Firefox.
   *   */

  button::-moz-focus-inner,
    [type="button"]::-moz-focus-inner,
    [type="reset"]::-moz-focus-inner,
    [type="submit"]::-moz-focus-inner {
      border-style: none;
      padding: 0;
    }

  /**
   *  * Restore the focus styles unset by the previous rule.
   *   */

  button:-moz-focusring,
    [type="button"]:-moz-focusring,
    [type="reset"]:-moz-focusring,
    [type="submit"]:-moz-focusring {
      outline: 1px dotted ButtonText;
    }

  /**
   *  * Change the border, margin, and padding in all browsers (opinionated).
   *   */

  fieldset {
    border: 1px solid #c0c0c0;
    margin: 0 2px;
    padding: 0;
  }

  /**
   *  * 1. Correct the text wrapping in Edge and IE.
   *   * 2. Correct the color inheritance from 'fieldset' elements in IE.
   *    * 3. Remove the padding so developers are not caught out when they zero out
   *     *    'fieldset' elements in all browsers.
   *      */

  legend {
    box-sizing: border-box; /* 1 */
    color: inherit; /* 2 */
    display: table; /* 1 */
    max-width: 100%; /* 1 */
    padding: 0; /* 3 */
    white-space: normal; /* 1 */
  }

  /**
   *  * 1. Add the correct display in IE 9-.
   *   * 2. Add the correct vertical alignment in Chrome, Firefox, and Opera.
   *    */

  progress {
    display: inline-block; /* 1 */
    vertical-align: baseline; /* 2 */
  }

  /**
   *  * Remove the default vertical scrollbar in IE.
   *   */

  textarea {
    overflow: auto;
  }

  /**
   *  * 1. Add the correct box sizing in IE 10-.
   *   * 2. Remove the padding in IE 10-.
   *    */

  [type="checkbox"],
    [type="radio"] {
      box-sizing: border-box; /* 1 */
      padding: 0; /* 2 */
    }

  /**
   *  * Correct the cursor style of increment and decrement buttons in Chrome.
   *   */

  [type="number"]::-webkit-inner-spin-button,
    [type="number"]::-webkit-outer-spin-button {
      height: auto;
    }

  /**
   *  * 1. Correct the odd appearance in Chrome and Safari.
   *   * 2. Correct the outline style in Safari.
   *    */

  [type="search"] {
    -webkit-appearance: textfield; /* 1 */
    outline-offset: -2px; /* 2 */
  }

  /**
   *  * Remove the inner padding and cancel buttons in Chrome and Safari on macOS.
   *   */

  [type="search"]::-webkit-search-cancel-button,
    [type="search"]::-webkit-search-decoration {
      -webkit-appearance: none;
    }

  /**
   *  * 1. Correct the inability to style clickable types in iOS and Safari.
   *   * 2. Change font properties to 'inherit' in Safari.
   *    */

    ::-webkit-file-upload-button {
      -webkit-appearance: button; /* 1 */
      font: inherit; /* 2 */
    }

  /* Interactive
   *    ========================================================================== */

  /*
   *  * Add the correct display in IE 9-.
   *   * 1. Add the correct display in Edge, IE, and Firefox.
   *    */

  details, /* 1 */
    menu {
      display: block;
    }

  /*
   *  * Add the correct display in all browsers.
   *   */

  summary {
    display: list-item;
  }

  /* Scripting
   *    ========================================================================== */

  /**
   *  * Add the correct display in IE 9-.
   *   */

  canvas {
    display: inline-block;
  }

  /**
   *  * Add the correct display in IE.
   *   */

  template {
    display: none;
  }

  /* Hidden
   *    ========================================================================== */

  /**
   *  * Add the correct display in IE 10-.
   *   */

  [hidden] {
    display: none;
  }

  #HW_frame_cont {
    margin-left: 6rem !important;
    margin-top: 3rem !important;
  }

  iframe.intercom-launcher-frame, .intercom-launcher, iframe.intercom-launcher-badge-frame {
    transition: bottom 1s ease 0s !important;
  }

  ${isStorybook() && StorybookStyle}

  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
  }

  button, a {
    font-weight: 500;
  }

  h1 {
    margin: 0.67em 0;
  }

  /** Resize headlines according typography settings */
  ${Object.keys(theme.typography.headline).map(
    (el) => `${el} {
      ${theme.typography.headline[el]};
    }`
  )};
`;

export default GlobalStyles;
